import { Box, Flex, Grid, HStack, IconButton, Spacer } from '@chakra-ui/react';
import { produce } from 'immer';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { assertPresence } from '@bonusly/runtime';

import { Conceal } from '../../../components/display/conceal';
import { FormErrorMessage } from '../../../components/form/form-error-message';
import { Button } from '../../../design-system/button';
import { Dropdown, DropdownInput, DropdownMenu, DropdownOption } from '../../../design-system/dropdown';
import { PlusOutline, TrashOutline } from '../../../design-system/icon';
import { Text } from '../../../design-system/typography';
import { CustomPropertyUserGroup, useCustomPropertyUserGroups } from '../../../queries/groups';

type AudienceSelectorProps = {
	error?: string;
	onChange: (value: { attr: string | undefined; value: string | undefined }[]) => void;
	value: { attr: string | undefined; value: string | undefined }[];
};

export const AudienceSelector: FC<AudienceSelectorProps> = ({ error, onChange, value }) => {
	const { data } = useCustomPropertyUserGroups();
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });

	assertPresence(data);

	const options = useMemo(() => {
		return Object.values(data)
			.flat()
			.reduce<Record<string, CustomPropertyUserGroup[]>>((groups, condition) => {
				if (!(condition.type in groups)) {
					return {
						...groups,
						[condition.type]: [condition],
					};
				}
				return {
					...groups,
					[condition.type]: [...groups[condition.type], condition],
				};
			}, {});
	}, [data]);

	// Create a top level audience selector set, which will be used to determine if the "Add Audience" button should be displayed.
	// If the user happens to have selected all available options, then the "Add Audience" button should be hidden.
	const availableOptions = useMemo(() => {
		return produce(options, (draft) => {
			Object.keys(options).forEach((attr) => {
				draft[attr] = draft[attr].filter((option) => !value.some((item) => item.attr === attr && item.value === option.key));
			});
		});
	}, [options, value]);

	const handleRemove = (index: number) => {
		onChange(value.filter((_, i) => i !== index));
	};

	const handleClear = () => {
		onChange([]);
	};

	const handleAdd = () => {
		onChange([...value, { attr: undefined, value: undefined }]);
	};

	const handleChangeAttr = (index: number, attr: string) => {
		const newValue = produce(value, (draft) => {
			draft[index] = {
				attr,
				value: undefined,
			};
		});

		onChange(newValue);
	};

	const handleChangeValue = (index: number, val: string) => {
		const newValue = produce(value, (draft) => {
			draft[index].value = val;
		});

		onChange(newValue);
	};

	return (
		<Box>
			<Grid rowGap={2} templateColumns="1fr 7rem 1fr 3.5rem">
				{value.map((condition, index) => {
					// Create a list of all of the selected options other than this one being rendered
					const alreadySelectedOptions = produce(value, (draft) => {
						draft.splice(index, 1);
					}).filter((item) => item.attr && item.value);

					// Build a set of options that can be selected from specific to this set
					const filteredOptions = produce(options, (draft) => {
						Object.keys(options).forEach((attr) => {
							draft[attr] = draft[attr].filter((option) => !alreadySelectedOptions.some((o) => o.attr === attr && o.value === option.key));
						});

						Object.keys(options).forEach((attr) => {
							if (draft[attr].length === 0) {
								delete draft[attr];
							}
						});
					});

					const valueOptions = condition.attr ? (filteredOptions[condition.attr] ?? []) : [];

					return (
						<Grid gridColumn="1 / span 4" templateColumns="subgrid" key={index}>
							<Grid
								gridColumn="1 / span 3"
								templateColumns="subgrid"
								border="1px solid"
								borderColor={error ? 'error.inputBorder' : 'border.input'}
								borderRadius="md"
								{...(!!error && {
									boxShadow: '0 0 0 1px #d82c2fff',
								})}
							>
								<Dropdown
									onChange={(value) => handleChangeAttr(index, value)}
									placeholder={t('audienceAttrPlaceholder')}
									renderOption={(value) => t('audienceLabel', { context: value, fallback: value.replaceAll('_', ' ') })}
									value={condition.attr}
								>
									<DropdownInput border="none" _active={{ border: 'none' }} w="full" textTransform="capitalize" />
									<DropdownMenu>
										{Object.keys(filteredOptions).map((attr) => (
											<DropdownOption key={attr} value={attr} textTransform="capitalize">
												{t('audienceLabel', { context: attr, fallback: attr.replaceAll('_', ' ') })}
											</DropdownOption>
										))}
									</DropdownMenu>
								</Dropdown>

								<HStack p={2} borderInline="1px solid" borderColor={error ? 'error.inputBorder' : 'border.input'}>
									<Text>{t('audienceEq')}</Text>
								</HStack>

								<Dropdown
									onChange={(value) => handleChangeValue(index, value)}
									placeholder={t('audienceValuePlaceholder')}
									value={condition.value}
									renderOption={(value) => valueOptions.find((o) => o.key === value)?.name}
								>
									<DropdownInput border="none" _active={{ border: 'none' }} w="full" />
									<DropdownMenu>
										{valueOptions.map((val) => (
											<DropdownOption key={val.key} value={val.key}>
												{val.name}
											</DropdownOption>
										))}
									</DropdownMenu>
								</Dropdown>
							</Grid>

							<Flex justify="flex-end">
								<IconButton aria-label="Remove condition" isRound onClick={() => handleRemove(index)}>
									<TrashOutline />
								</IconButton>
							</Flex>
						</Grid>
					);
				})}
			</Grid>

			<FormErrorMessage>{error}</FormErrorMessage>

			<HStack mt={2}>
				<Conceal if={!Object.keys(availableOptions).length}>
					<Button leftIcon={<PlusOutline size="sm" />} variant="ghost" onClick={handleAdd}>
						{t('audienceAdd')}
					</Button>
				</Conceal>
				<Spacer />
				<Button colorScheme="secondary" variant="ghost" onClick={handleClear}>
					{t('audienceClear')}
				</Button>
			</HStack>
		</Box>
	);
};
